import {Analytics} from '../../services/analytics/Analytics';

const ANALYTICS_DELAY_MS = 300;
const ANALYTICS_SCRIPT_LOAD_DELAY_MS = 1000;

/**
 * Analytics page events are tracked based on changes in route - see the
 * callsite for this method - currently in RouteWithLayout.tsx.
 * Unless you are on a route in the site that doesn't go through RouteWithLayout,
 * you probably shouldn't be calling this so that we don't duplicate page()
 * analytics events
 */
export function trackPage(properties: object, options: object) {
  window.analytics?.page?.(properties, options);
}

// helper function to wait for consent/analytic scripts
function waitForAnalyticsScriptsOrTimeout(timeoutMs: number): Promise<boolean> {
  return new Promise(resolve => {
    let resolved = false;

    // Poll until OneTrust is ready
    const poll = setInterval(() => {
      // use OneTrust as a proxy since it loads the analytics scripts
      if ((window as {OneTrust?: unknown}).OneTrust != null) {
        clearInterval(poll);
        if (!resolved) {
          resolved = true;
          resolve(true); // OneTrust loaded
        }
      }
    }, 50);

    // Fallback timeout
    setTimeout(() => {
      if (!resolved) {
        clearInterval(poll);
        resolved = true;
        resolve(false); // Timeout hit first
      }
    }, timeoutMs);
  });
}

/**
 * Does a forced redirect while sending analytics events
 * that would be lost once the user returns back to the app.
 *
 * This is CRITICAL for first-touch analytics, which will often
 * lead an account-less user to a page unauthenticated, triggering
 * a redirect that would otherwise lose referrer information on
 * how the user was led to the page initially.
 */
export function forceRedirect(redirectLink: string, ensureAnalytics = true) {
  // don't lose an analytics event for the redirect, first touch is most important!
  if (ensureAnalytics) {
    // allow page event to trigger
    trackPage({name: 'External Redirect', redirectLink}, {});

    // we need to accomodate for users who have variable network connections
    // because they need some time to load the analytics scripts
    waitForAnalyticsScriptsOrTimeout(ANALYTICS_SCRIPT_LOAD_DELAY_MS).finally(
      () => {
        // Delay following the link reference by ANALYTICS_DELAY_MS in order to give time
        // for the analytics event to trigger before unloading the page, which
        // will cancel the analytics network call.
        //
        // This is the same strategy used by segment's own trackLink, now applied to our page events:
        // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track-link
        setTimeout(() => {
          // eslint-disable-next-line wandb/no-unprefixed-urls
          window.location.href = redirectLink;
        }, ANALYTICS_DELAY_MS);
      }
    );
  } else {
    // eslint-disable-next-line wandb/no-unprefixed-urls
    window.location.href = redirectLink;
  }
}

/** @deprecated
 * We are not removing this function yet, but its usage has been replaced by the
 * route based calls to trackPage.
 * We can remove this call (and the places in code that call it) once we've verified
 * that we are getting proper pageview tracking through trackPage
 */
export function trackPageViewed(page: string, tab?: string) {
  window.analytics?.page(page, {tab});
}

export function trackBreadcrumbClicked(destination: string) {
  Analytics.track('Top nav breadcrumb clicked', {destination});
}

export function trackModelRegistryClicked(location: string) {
  Analytics.track('Model registry clicked', {
    location,
  });
}

export function trackGlobalRegistryClicked(location: string) {
  Analytics.track('Global registry clicked', {
    location,
  });
}

export function trackProfileMenuClicked(location: string) {
  Analytics.track('Profile menu clicked', {
    location,
  });
}

export function trackAllProjectsClicked(location: string) {
  Analytics.track('All projects clicked', {
    location,
  });
}

export function trackLaunchClicked(location: string) {
  Analytics.track('Launch clicked', {
    location,
  });
}

export function trackModelRegistryAccessControlsInteraction(opts: {
  [key: string]: any;
}) {
  Analytics.track('Model registry access controls interaction', opts);
}

export function trackCreateTriggerClicked(location: string) {
  Analytics.track('Create Trigger button clicked', {location});
}

export function trackLaunchConfigUpdated(opts: {[key: string]: any}) {
  Analytics.track('Trigger config updated', {opts});
}

export function trackBannerButtonClicked(opts: {[key: string]: any}) {
  Analytics.track('Banner Button Clicked', {opts});
}
